import {
  TOPIC_CONTENT_SOURCES,
  type TopicContentSource,
} from "./topic-content-sources";
import { TOPIC_GUIDES, type TopicGuide } from "./topic-guides";

export const COST_OF_LIVING_GUIDE = {
  topicId: "cost-of-living",
  locale: "ko",
  publicationStatus: "korean_test_publish",
  risk: "R2",
  checkedAt: "2026-07-16",
  jurisdictionLabel: "호주 전역·NSW 지원 검색 예시",
  summary:
    "생활비가 부족하면 모든 지출을 한꺼번에 줄이려 하기보다 앞으로 7일과 30일의 rent·mortgage, food, medicine, utilities와 필수 transport를 먼저 보호하세요. bank statement와 bill을 이용해 실제 예산을 만들고, 납부 전에 provider의 hardship·payment plan을 요청합니다. Services Australia Payment and Service Finder와 NSW Savings Finder는 가능한 payment·service·rebate를 찾는 출발점이지만 자격이나 지급을 보장하지 않습니다. 당장 식비·주거·공과금이 없으면 무료 National Debt Helpline과 emergency relief를 조기에 이용하고, advance payment·BNPL·payday loan으로 반복 적자를 숨기지 마세요.",
  keyPoints: [
    "먼저 7일 안에 필요한 주거·식비·의약품·공과금·필수 교통비와 due date를 적으세요.",
    "bank statement와 실제 bill을 사용해 irregular expense까지 포함한 현실적인 budget을 만드세요.",
    "bill을 못 낼 것 같으면 due date 전 provider에 hardship·extension·instalment를 요청하고 reference number를 받으세요.",
    "전기·가스 retailer는 NSW 등 적용 지역의 payment difficulty 고객에게 flexible payment와 hardship support를 제공해야 합니다.",
    "Payment and Service Finder·Savings Finder 결과는 후보 목록이며 실제 자격은 각 program에서 다시 확인해야 합니다.",
    "Centrelink advance는 추가 소득이 아니라 앞으로 받을 payment의 일부를 미리 받는 방식일 수 있습니다.",
    "식비·주거·약·교통이 당장 부족하면 emergency relief와 무료 financial counselling을 먼저 이용하세요.",
    "payday loan, credit-card cash advance와 새 BNPL로 지속적인 생활비 적자를 막으면 비용과 부채가 커질 수 있습니다.",
  ],
  sections: [
    {
      id: "triage-next-week",
      title: "1. 앞으로 7일의 필수지출과 중단 위험을 먼저 분류하세요",
      body: [
        "현재 bank balance, 다음 income date와 7일·30일 안의 모든 due date를 한 화면에 적으세요. rent·mortgage, food, medicine, electricity·gas, phone, 필수 출근·통학 transport를 먼저 표시하고, late fee·disconnection·eviction·insurance lapse·vehicle repossession처럼 결과가 큰 항목을 구분하세요. 자동이체가 잔액 부족으로 반복 실패하지 않도록 payment date 변경 가능성도 확인하세요.",
        "모든 subscription과 선택지출을 즉시 없애는 것보다 household가 계속 생활하고 일할 수 있는 최소비용을 먼저 계산하세요. 이미 식비·주거·medicine·utility가 없는 긴급상황이면 budget을 완성할 때까지 기다리지 말고 무료 National Debt Helpline, local emergency relief 또는 state housing support를 이용하세요.",
      ],
      checklist: [
        "현재 balance와 다음 income 날짜·금액",
        "7일·30일 안의 모든 bill과 due date",
        "rent·mortgage·food·medicine·utilities·필수 transport",
        "late fee·disconnection·housing·insurance 위험",
        "자동이체 실패·overdraw fee 가능성",
        "오늘 필요한 emergency food·housing·bill support",
      ],
      sourceIds: [
        "cost-of-living-source-urgent-help",
        "cost-of-living-source-bills-fines",
      ],
    },
    {
      id: "build-real-budget",
      title: "2. 실제 거래내역으로 가계예산을 만들고 줄일 순서를 정하세요",
      body: [
        "최근 몇 달 bank statement, payslip와 bill을 이용해 income과 fixed·variable expense를 기록하세요. weekly·fortnightly·monthly 항목의 기간을 맞추고 annual registration, insurance, school, medical, repair처럼 매달 나오지 않는 비용도 월평균으로 나누세요. 예상치가 아니라 실제 spending을 사용해야 감당 가능한 금액을 알 수 있습니다.",
        "먼저 사용하지 않는 subscription, duplicate service, avoidable fee와 비싼 plan을 찾고, insurance·energy·phone·internet은 coverage·contract·exit fee와 총비용을 비교한 뒤 변경하세요. grocery·fuel·transport는 단가뿐 아니라 이동거리·waste·시간을 함께 보세요. 작은 절약만으로 구조적 적자가 해결되지 않으면 income support·hardship·housing 또는 debt advice를 함께 검토하세요.",
      ],
      checklist: [
        "최근 bank statement와 모든 income",
        "weekly·fortnightly·monthly 기간 통일",
        "annual·quarterly irregular expense 월평균",
        "subscription·fee·duplicate service 목록",
        "plan 변경의 contract·exit fee·coverage",
        "현재 적자가 일시적인지 반복되는지",
      ],
      sourceIds: [
        "cost-of-living-source-budget",
        "cost-of-living-source-living-help",
      ],
    },
    {
      id: "contact-providers",
      title: "3. 납부 전에 provider·landlord·council에 변경을 요청하세요",
      body: [
        "bill을 제때 전액 낼 수 없으면 due date 전 provider의 hardship 또는 payment difficulty team에 연락하세요. 상황, income timing과 감당 가능한 amount를 설명하고 extension, instalment, due-date change, fee waiver 또는 hardship program을 요청하세요. 합의한 amount·date·interest·fee·service status와 reference number를 서면으로 받으세요.",
        "전기·가스 retailer는 National Energy Retail Law가 적용되는 NSW·QLD·ACT·SA·TAS에서 flexible payment options와 hardship program을 제공해야 합니다. rent·mortgage·fine·tax·insurance·phone 등은 서로 다른 규칙과 결과가 있으므로 모든 bill에 같은 해결책이 적용된다고 생각하지 말고 해당 provider의 공식 process를 확인하세요. court·termination·disconnection notice는 별도 긴급기한이 있을 수 있습니다.",
      ],
      checklist: [
        "provider official hardship contact",
        "account·bill number와 due date",
        "어려움의 원인과 income timing",
        "지속 가능한 instalment amount",
        "extension·fee waiver·plan 변경 요청",
        "written agreement와 reference number",
        "notice·disconnection·legal deadline 여부",
      ],
      sourceIds: [
        "cost-of-living-source-bills-fines",
        "cost-of-living-source-energy-rights",
      ],
    },
    {
      id: "check-payments-and-savings",
      title: "4. 정부 payment·concession·rebate 후보를 공식 도구로 확인하세요",
      body: [
        "Services Australia Payment and Service Finder에서 household, work, study, caring, disability와 income 상황에 따라 possible payment·service를 확인하세요. tool 결과만으로 자격을 확정하지 말고 각 payment page의 residence, income·assets, claim date와 evidence를 다시 확인하세요. 이미 Centrelink payment를 받는 경우 advance payment는 추가 payment가 아니라 미래 payment 일부를 미리 받는 방식일 수 있으므로 이후 payment 감소와 repayment 영향을 확인하세요.",
        "NSW Savings Finder에서 energy·water·transport·health·children·seniors 등 현재 rebate와 concession 후보를 확인하세요. Family Energy Rebate, Low Income Household Rebate, EAPA, transport concession처럼 program마다 card·payment·residence·account holder 조건과 신청기간이 다릅니다. 링크를 통해 실제 program page로 이동해 eligibility와 current amount를 확인한 뒤 신청 기록을 보관하세요.",
      ],
      checklist: [
        "household·work·study·caring 상황",
        "residence·income·assets·concession card 자료",
        "Payment and Service Finder 결과 목록",
        "NSW Savings Finder의 energy·transport·health 후보",
        "각 program의 eligibility·claim date·evidence",
        "advance payment의 향후 payment 감소 영향",
      ],
      sourceIds: [
        "cost-of-living-source-payment-finder",
        "cost-of-living-source-savings-finder",
        "cost-of-living-source-urgent-help",
      ],
    },
    {
      id: "use-emergency-help-and-review",
      title: "5. 긴급지원 후 30일마다 budget과 지원상태를 다시 점검하세요",
      body: [
        "식비·transport·chemist·utility·school cost가 즉시 부족하면 emergency relief provider, community centre와 state housing support를 이용하세요. Moneysmart는 food parcel·voucher, transport·chemist voucher, part-payment of utility bill과 housing crisis support 경로를 안내합니다. 서비스 재고·자격·운영시간은 변할 수 있으므로 방문 전에 확인하고, 즉각적인 homelessness risk는 NSW Link2home 등 현재 state emergency housing channel을 이용하세요.",
        "지원금이나 한 번의 절약으로 끝내지 말고 30일 뒤 actual spending, overdue balance, hardship agreement, rebate claim과 다음 큰 annual expense를 다시 확인하세요. 반복 적자가 남으면 free financial counsellor와 rent·debt·employment·benefit 문제를 함께 검토하세요. 돈을 아껴준다며 선불비용·bank login·인증코드를 요구하는 rebate·refund scam도 공식 기관을 직접 확인하세요.",
      ],
      sourceIds: [
        "cost-of-living-source-urgent-help",
        "cost-of-living-source-living-help",
      ],
    },
  ],
  faqs: [
    {
      id: "cost-of-living-what-first",
      question: "돈이 부족할 때 어떤 bill부터 내야 하나요?",
      answer:
        "개인 상황에 따라 다르지만 주거, food, medicine, utilities와 일을 유지하는 필수 transport처럼 즉각적인 생활·자산 위험이 큰 항목을 먼저 확인하세요. 모든 due date와 결과를 적고 무료 financial counsellor에게 전체 목록을 보여주세요.",
      sourceIds: [
        "cost-of-living-source-urgent-help",
        "cost-of-living-source-bills-fines",
      ],
    },
    {
      id: "cost-of-living-before-due",
      question: "bill이 아직 연체되지 않았는데 hardship를 요청할 수 있나요?",
      answer:
        "네. 납부가 어렵다는 것을 알게 된 즉시 provider에 연락하는 것이 좋습니다. 감당 가능한 amount와 원하는 변경을 말하고 agreement·fee·service status를 서면으로 받으세요.",
      sourceIds: [
        "cost-of-living-source-bills-fines",
        "cost-of-living-source-energy-rights",
      ],
    },
    {
      id: "cost-of-living-centrelink-finder",
      question: "Payment and Service Finder 결과가 나오면 payment를 받을 수 있나요?",
      answer:
        "도구는 가능한 payment와 service를 찾는 출발점입니다. 실제 자격·금액·시작일은 residence, income·assets, household와 claim evidence에 따라 결정되므로 각 official payment page에서 다시 확인하세요.",
      sourceIds: ["cost-of-living-source-payment-finder"],
    },
    {
      id: "cost-of-living-advance",
      question: "Centrelink advance payment는 추가 지원금인가요?",
      answer:
        "일반적으로 이미 받을 payment의 일부를 일찍 받는 방식이며 추가 소득이 아닐 수 있습니다. 이후 payment에서 회수되는 방식과 자격을 Services Australia에서 확인하고 다음 달 budget에 반영하세요.",
      sourceIds: ["cost-of-living-source-urgent-help"],
    },
    {
      id: "cost-of-living-nsw-savings",
      question: "NSW Savings Finder에 나온 rebate는 자동으로 지급되나요?",
      answer:
        "아닙니다. Finder는 후보를 모아 보여주며 각 rebate·concession의 card, income, residence, account holder와 신청기한을 별도 program page에서 확인하고 신청해야 합니다.",
      sourceIds: ["cost-of-living-source-savings-finder"],
    },
    {
      id: "cost-of-living-energy-disconnection",
      question: "전기요금을 못 내서 단전될까 걱정됩니다.",
      answer:
        "통지를 기다리지 말고 retailer hardship team에 즉시 연락해 flexible payment와 hardship program을 요청하세요. warning·disconnection risk가 임박했다면 energy ombudsman과 emergency assistance 경로를 함께 확인하세요.",
      sourceIds: [
        "cost-of-living-source-energy-rights",
        "cost-of-living-source-savings-finder",
      ],
    },
    {
      id: "cost-of-living-emergency-food",
      question: "오늘 먹을 음식이나 약을 살 돈이 없습니다.",
      answer:
        "budget 완성을 기다리지 말고 Moneysmart가 안내하는 emergency relief, food provider, community organisation 또는 National Debt Helpline에 연락하세요. 운영시간·지역·재고는 방문 전에 확인하세요.",
      sourceIds: ["cost-of-living-source-urgent-help"],
    },
    {
      id: "cost-of-living-payday-loan",
      question: "다음 급여일까지 payday loan이나 BNPL로 버텨도 될까요?",
      answer:
        "고비용 credit로 반복 적자를 막으면 fee와 다음 달 부족액이 커질 수 있습니다. 먼저 provider hardship, emergency relief와 무료 financial counselling을 확인하고 total repayment와 due date를 이해하기 전에는 새 credit를 서두르지 마세요.",
      sourceIds: [
        "cost-of-living-source-living-help",
        "cost-of-living-source-bills-fines",
      ],
    },
    {
      id: "cost-of-living-budget-irregular",
      question: "rego·보험·학교비처럼 가끔 나오는 비용은 budget에 어떻게 넣나요?",
      answer:
        "예상 annual 또는 quarterly amount를 월·fortnight·week 단위로 나눠 별도 savings category에 넣으세요. 과거 bill을 사용하고 amount가 바뀌면 actual cost로 조정하세요.",
      sourceIds: ["cost-of-living-source-budget"],
    },
    {
      id: "cost-of-living-not-enough",
      question: "모든 절약을 해도 매달 계속 적자입니다.",
      answer:
        "작은 지출만 더 줄이기보다 housing, income, debt, benefits와 큰 contracts를 함께 검토해야 합니다. 무료 financial counsellor에게 전체 budget과 debt를 보여주고 hardship·government support·legal 또는 employment help가 필요한지 확인하세요.",
      sourceIds: [
        "cost-of-living-source-living-help",
        "cost-of-living-source-urgent-help",
      ],
    },
  ],
  sourceIds: [
    "cost-of-living-source-living-help",
    "cost-of-living-source-urgent-help",
    "cost-of-living-source-bills-fines",
    "cost-of-living-source-budget",
    "cost-of-living-source-payment-finder",
    "cost-of-living-source-savings-finder",
    "cost-of-living-source-energy-rights",
  ],
  additionalChecks: [
    "payment·rebate·concession의 amount, income·residence 조건과 claim date는 신청 직전 공식 program page에서 확인하세요.",
    "rent·mortgage·fine·tax·insurance·utility의 notice와 legal deadline은 서로 다르므로 실제 문서를 확인하세요.",
    "provider hardship agreement의 amount, fee, interest, term과 service·credit-reporting 영향을 서면으로 확인하세요.",
    "emergency relief·housing·food service의 current opening hours, area와 availability를 방문 전에 확인하세요.",
    "Centrelink advance가 향후 payment에 미치는 영향과 다른 debt·deduction을 Services Australia에서 확인하세요.",
    "한 달 후 actual spending과 support 결과를 다시 검토하고 반복 적자는 무료 financial counsellor와 구조적으로 점검하세요.",
  ],
  disclaimer:
    "이 안내는 호주의 생활비, 가계예산, bill hardship와 긴급지원 탐색에 관한 일반 생활정보입니다. 특정 Centrelink payment, NSW rebate, emergency relief, fee waiver 또는 hardship 승인을 확정하지 않습니다. payment·program·provider 조건과 지원 가능성은 residence, income, household, account와 현재 자금에 따라 달라지므로 Services Australia, Service NSW, provider와 Moneysmart의 최신 공식 안내를 확인하세요.",
} as const satisfies TopicGuide;

export const COST_OF_LIVING_SOURCES = [
  {
    sourceId: "cost-of-living-source-living-help",
    topicId: "cost-of-living",
    title: "Cost of living help",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/budgeting/cost-of-living-help",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 30일 갱신된 Moneysmart 공식 본문에서 spending review, subscription·insurance·energy·grocery·fuel 절감과 어려울 때 지원경로 확인",
    reviewStatus: "live_verified",
    claimScope: "생활비 절감과 큰 household cost 비교, 구조적 적자 시 지원 연결",
    followUp: "개별 provider offer, comparison result와 current contract·exit fee 재확인",
  },
  {
    sourceId: "cost-of-living-source-urgent-help",
    topicId: "cost-of-living",
    title: "Urgent help with money",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/managing-debt/urgent-help-with-money",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 본문에서 National Debt Helpline, food·bill·housing emergency relief, crisis·advance payment와 state support 확인",
    reviewStatus: "live_verified",
    claimScope: "식비·주거·공과금·transport의 긴급 생활지원과 무료 상담 시작점",
    followUp: "각 service의 current hours, location, stock, eligibility와 emergency contact 재확인",
  },
  {
    sourceId: "cost-of-living-source-bills-fines",
    topicId: "cost-of-living",
    title: "Problems paying your bills and fines",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/managing-debt/problems-paying-your-bills-and-fines",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 공식 본문에서 provider 조기 연락, extension·instalment·hardship와 bill 종류별 도움 확인",
    reviewStatus: "live_verified",
    claimScope: "공과금·통신·insurance·fine 등 bill payment difficulty의 초기 대응",
    followUp: "bill 종류별 provider policy, legal deadline, fee와 ombudsman 관할 재확인",
  },
  {
    sourceId: "cost-of-living-source-budget",
    topicId: "cost-of-living",
    title: "How to do a budget",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/budgeting/how-to-do-a-budget",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 공식 본문에서 income·expense 수집, period 통일, irregular cost와 budget review 확인",
    reviewStatus: "live_verified",
    claimScope: "실제 household income·spending 기반 budget 작성과 정기 검토",
    followUp: "최근 statement·bill의 actual amount와 household 변경을 budget에 재반영",
  },
  {
    sourceId: "cost-of-living-source-payment-finder",
    topicId: "cost-of-living",
    title: "Payment and Service Finder",
    publisher: "Services Australia",
    url: "https://www.servicesaustralia.gov.au/payment-and-service-finder",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Services Australia 공식 도구 본문에서 Centrelink payment·service 탐색과 결과 후 개별 eligibility 확인 경로 확인",
    reviewStatus: "live_verified",
    claimScope: "household·work·study·caring 상황별 Australian Government payment·service 후보 탐색",
    followUp: "각 payment의 current residence·income·assets·claim date·evidence를 개별 페이지에서 재확인",
  },
  {
    sourceId: "cost-of-living-source-savings-finder",
    topicId: "cost-of-living",
    title: "Savings Finder",
    publisher: "Service NSW",
    url: "https://www.service.nsw.gov.au/campaign/savings-finder",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Service NSW 공식 Savings Finder에서 energy·water·driving·transport·health·children·seniors rebate·concession 후보 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW household의 current rebate·concession·savings program 탐색 시작점",
    followUp: "각 linked program의 eligibility, amount, application window와 account-holder 조건 재확인",
  },
  {
    sourceId: "cost-of-living-source-energy-rights",
    topicId: "cost-of-living",
    title: "Your energy rights",
    publisher: "Australian Energy Regulator",
    url: "https://www.aer.gov.au/consumers/understanding-energy/your-energy-rights",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "AER 공식 본문에서 NSW 등 NERL 지역의 flexible payment, hardship program, fee·billing·complaint 권리 확인",
    reviewStatus: "live_verified",
    claimScope: "energy bill payment difficulty의 retailer support와 consumer rights",
    followUp: "retailer hardship policy, disconnection notice, ombudsman과 state 적용범위 재확인",
  },
] as const satisfies readonly TopicContentSource[];

const mutableGuides = TOPIC_GUIDES as unknown as TopicGuide[];
if (!mutableGuides.some((guide) => guide.topicId === COST_OF_LIVING_GUIDE.topicId)) {
  mutableGuides.push(COST_OF_LIVING_GUIDE);
}

const mutableSources = TOPIC_CONTENT_SOURCES as unknown as TopicContentSource[];
for (const source of COST_OF_LIVING_SOURCES) {
  if (!mutableSources.some((existing) => existing.sourceId === source.sourceId)) {
    mutableSources.push(source);
  }
}
